// React-Client#141: let a URL content item pass context to the customer's web
// server, so an admin can author e.g.
//
//   https://intranet.example.com/policy?org={{OrgId}}&user={{UserId}}
//
// and have it resolved per viewer at open time.
//
// Three rules drive the design:
//
//   1. ALLOWLIST ONLY. We substitute a fixed, documented set of tokens. An
//      unrecognised token is left in the URL verbatim rather than silently
//      blanked, so a typo is visible to the author instead of producing a
//      plausible-looking request with a missing value.
//
//   2. THE SESSION TOKEN IS NEVER AVAILABLE. There is deliberately no {{Token}}
//      or {{JWT}}. These URLs point at third-party servers; handing them the
//      viewer's dbServer session JWT would let that server act as the user.
//      This is the single most important property of this file.
//
//   3. SUBSTITUTE FIRST, THEN VALIDATE. Callers must run the existing
//      isSafeHttpUrl() check on the EXPANDED url, never the template, so the
//      scheme guarantee from #339 holds on what is actually navigated to.

// Token name -> how to read it off the context object. Extend deliberately;
// every addition here is a value handed to a third party.
const TOKENS = {
  OrgId: (c) => c.orgId,
  UserId: (c) => c.userId,
  UserName: (c) => c.name,
  IsSandbox: (c) => (c.isSandbox ? "true" : "false"),
  ContentId: (c) => c.contentId,
  ContentName: (c) => c.contentName,
};

/** The tokens an author may use, for help text and validation UI. */
export function supportedTokens() {
  return Object.keys(TOKENS);
}

/**
 * Build the substitution context from the app's user object and the content
 * item being opened. Kept separate from expansion so callers cannot
 * accidentally pass the whole user object (which carries the session token)
 * into the expander.
 */
export function buildUrlContext(userData, content) {
  const u = userData || {};
  const c = content || {};
  return {
    orgId: u.orgId,
    userId: u.userId,
    name: u.name,
    isSandbox: u.IsSandbox === true || u.IsSandbox === 1,
    contentId: c.ContentId != null ? String(c.ContentId) : undefined,
    contentName: c.ContentName,
  };
}

/**
 * Replace {{Token}} placeholders in a content URL.
 *
 * Values are URL-encoded, so a display name with a space or an ampersand
 * cannot break out of its query parameter. Unknown tokens and tokens whose
 * value is missing are left exactly as written.
 */
export function expandContentUrl(url, context) {
  if (typeof url !== "string" || url.indexOf("{{") === -1) return url;
  const ctx = context || {};
  return url.replace(/\{\{\s*([A-Za-z][A-Za-z0-9_]*)\s*\}\}/g, (match, name) => {
    const reader = TOKENS[name];
    if (!reader) return match; // unknown token: leave it visible
    const value = reader(ctx);
    if (value === undefined || value === null || value === "") return match;
    return encodeURIComponent(String(value));
  });
}

/**
 * Does this URL reference any token we do not know about? Lets an authoring
 * screen warn before save rather than shipping a broken link.
 */
export function unknownTokensIn(url) {
  if (typeof url !== "string") return [];
  const found = [];
  const re = /\{\{\s*([A-Za-z][A-Za-z0-9_]*)\s*\}\}/g;
  let m;
  while ((m = re.exec(url)) !== null) {
    if (!TOKENS[m[1]] && found.indexOf(m[1]) === -1) found.push(m[1]);
  }
  return found;
}
